import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { parseProduct, type Product } from '@/lib/marketplace';
import type { NostrEvent } from '@nostrify/nostrify';

export interface ProductEvent {
  event: NostrEvent;
  product: Product;
}

/** Fetch the current user's own kind:30018 product listings */
export function useMyProducts(stallId?: string) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery<ProductEvent[]>({
    queryKey: ['my-products', user?.pubkey, stallId],
    queryFn: async () => {
      if (!user?.pubkey) return [];
      const filter: Record<string, unknown> = {
        kinds: [30018],
        authors: [user.pubkey],
        limit: 500,
      };
      const events = await nostr.query(
        [filter as Parameters<typeof nostr.query>[0][0]],
        { signal: AbortSignal.timeout(10000) }
      );
      const result: ProductEvent[] = [];
      for (const event of events) {
        const product = parseProduct(event.content);
        if (product) {
          // If a stallId filter is given, restrict to that stall
          if (!stallId || product.stall_id === stallId) {
            result.push({ event, product });
          }
        }
      }
      return result;
    },
    enabled: !!user?.pubkey,
    staleTime: 30000,
  });
}
